import type { ReactNode } from "react";
import type { Basemap } from "./MapView";

export interface LegendEntry { key: string; label: string; color: string; n: number }

interface HeaderProps {
  /** Programme name, e.g. "Volunteer Lake Monitoring". */
  program: string;
  /** Short form used on narrow screens, e.g. "Lake Monitoring". */
  programShort: string;
  legend: LegendEntry[];
  /** Which legend entry, if any, is being used as an active filter. */
  activeLegend?: string | null;
  onLegendClick?: (key: string) => void;
  basemap: Basemap;
  onBasemap: (b: Basemap) => void;
  refreshing: boolean;
  lastRefresh: "updated" | "current" | null;
  onRefresh: () => void;
  /** Slot for per-app header controls, such as the About button. */
  extra?: ReactNode;
}

export function Header({
  program, programShort, legend, activeLegend, onLegendClick,
  basemap, onBasemap, refreshing, lastRefresh, onRefresh, extra,
}: HeaderProps) {
  return (
    <header className="header">
      <a className="brand" href="/" aria-label={`${program} — home`}>
        <img
          className="brand-logo"
          src="/totm-logo.png"
          alt="Tip of the Mitt Watershed Council"
          width={44}
          height={44}
        />
        <span className="brand-lockup">
          <span className="brand-org">Tip of the Mitt Watershed Council</span>
          <span className="brand-title">
            <span className="brand-title-full">{program}</span>
            <span className="brand-title-short">{programShort}</span>
          </span>
        </span>
      </a>

      {legend.length > 0 && (
        <div className="legend" aria-label="Status legend">
          {legend.map((l) => {
            const on = activeLegend === l.key;
            const Tag = onLegendClick ? "button" : "span";
            return (
              <Tag
                key={l.key}
                className={"legend-item" + (on ? " on" : "")}
                {...(onLegendClick
                  ? { onClick: () => onLegendClick(l.key), "aria-pressed": on, type: "button" as const }
                  : {})}
              >
                <i style={{ background: l.color }} />
                <span className="legend-label">{l.label}</span>
                <b>{l.n}</b>
              </Tag>
            );
          })}
        </div>
      )}

      <div className="header-actions">
        {extra}
        <div className="toggle" role="group" aria-label="Basemap">
          {(["satellite", "light"] as Basemap[]).map((b) => (
            <button
              key={b}
              className={basemap === b ? "on" : ""}
              onClick={() => onBasemap(b)}
              aria-pressed={basemap === b}
            >
              {b === "satellite" ? "Satellite" : "Map"}
            </button>
          ))}
        </div>
        <button
          className={"btn" + (lastRefresh ? " btn-done" : "")}
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing
            ? "Refreshing…"
            : lastRefresh === "updated"
              ? "Updated ✓"
              : lastRefresh === "current"
                ? "Up to date"
                : "Refresh"}
        </button>
      </div>
    </header>
  );
}

export interface FootItem { label: string; value: string; note?: string; title?: string }

export function Footer({ items, right, credit }: {
  items: FootItem[];
  right?: ReactNode;
  /** Programme credit, pushed to the right ahead of `right`. */
  credit?: ReactNode;
}) {
  return (
    <footer className="footer">
      {items.map((it) => (
        <span className="foot-item" key={it.label} title={it.title}>
          <span className="foot-label">{it.label}</span>
          <span className="foot-value">{it.value}</span>
          {it.note && <span className="foot-note">{it.note}</span>}
        </span>
      ))}
      {credit && <span className="foot-item foot-right">{credit}</span>}
      {right && (
        <span className={"foot-item" + (credit ? "" : " foot-right")}>{right}</span>
      )}
    </footer>
  );
}

export function Splash({ children, tone }: { children: ReactNode; tone?: "error" }) {
  return (
    <div className={"splash" + (tone ? " " + tone : "")}>
      <div>{children}</div>
    </div>
  );
}

export type Pane = "map" | "list" | "trend";

export function Tabs({ pane, onPane, trendLabel = "Trends" }: {
  pane: Pane; onPane: (p: Pane) => void; trendLabel?: string;
}) {
  const labels: Record<Pane, string> = { map: "Map", list: "Sites", trend: trendLabel };
  return (
    <nav className="tabs" role="tablist" aria-label="View">
      {(["map", "list", "trend"] as Pane[]).map((p) => (
        <button
          key={p}
          role="tab"
          aria-selected={pane === p}
          className={pane === p ? "on" : ""}
          onClick={() => onPane(p)}
        >
          {labels[p]}
        </button>
      ))}
    </nav>
  );
}
